import { create } from 'zustand';
import {
  getLocations,
  getLocationById,
  createLocation,
  updateLocation,
  deleteLocation,
  getRooms,
  getRoomById,
  createRoom,
  updateRoom,
  deleteRoom,
  getTrainers,
  getTrainerById,
  createTrainer,
  updateTrainer,
  deleteTrainer,
  getILTStats,
  getAvailableInstructors,
  addTrainerFromUser,
  Location,
  Room,
  Trainer,
  ILTStats,
  Instructor,
} from '@/services/PagesServices/SuperAdminILTService';

interface ILTResourceManagementState {
  locations: Location[] | null;
  rooms: Room[] | null;
  trainers: Trainer[] | null;
  stats: ILTStats | null;
  instructors: Instructor[] | null;
  loading: boolean;
  error: string | null;

  // Fetch methods
  fetchLocations: (search?: string, status?: string) => Promise<void>;
  fetchRooms: (search?: string, status?: string, locationId?: number, type?: string) => Promise<void>;
  fetchTrainers: (search?: string, status?: string, availability?: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchAvailableInstructors: () => Promise<void>;

  // Location CRUD
  addLocation: (locationData: Partial<Location>) => Promise<{ success: boolean; message?: string }>;
  editLocation: (id: number, locationData: Partial<Location>) => Promise<{ success: boolean; message?: string }>;
  removeLocation: (id: number) => Promise<{ success: boolean; message?: string }>;

  // Room CRUD
  addRoom: (roomData: Partial<Room>) => Promise<{ success: boolean; message?: string }>;
  editRoom: (id: number, roomData: Partial<Room>) => Promise<{ success: boolean; message?: string }>;
  removeRoom: (id: number) => Promise<{ success: boolean; message?: string }>;

  // Trainer CRUD
  addTrainer: (trainerData: Partial<Trainer>) => Promise<{ success: boolean; message?: string }>;
  editTrainer: (id: number, trainerData: Partial<Trainer>) => Promise<{ success: boolean; message?: string }>;
  removeTrainer: (id: number) => Promise<{ success: boolean; message?: string }>;

  // Instructor Management
  createTrainerFromUser: (userId: string, trainerData?: Partial<Trainer>) => Promise<{ success: boolean; message?: string }>;

  // Clear error
  clearError: () => void;
}

const useSuperAdminILTStore = create<ILTResourceManagementState>((set, get) => ({
  locations: null,
  rooms: null,
  trainers: null,
  stats: null,
  instructors: null,
  loading: false,
  error: null,

  // ===== FETCH METHODS =====

  fetchLocations: async (search?: string, status?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getLocations(search, status);

      if (response.success && response.data) {
        set({ locations: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch locations', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRooms: async (search?: string, status?: string, locationId?: number, type?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getRooms(search, status, locationId, type);

      if (response.success && response.data) {
        set({ rooms: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch rooms', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchTrainers: async (search?: string, status?: string, availability?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getTrainers(search, status, availability);

      if (response.success && response.data) {
        set({ trainers: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch trainers', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchStats: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getILTStats();

      if (response.success && response.data) {
        set({ stats: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch stats', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // ===== LOCATION CRUD =====

  addLocation: async (locationData: Partial<Location>) => {
    try {
      set({ loading: true, error: null });
      const response = await createLocation(locationData);

      if (response.success) {
        // Refresh locations list
        await get().fetchLocations();
        return { success: true, message: response.message || 'Location created successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to create location';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to create location';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  editLocation: async (id: number, locationData: Partial<Location>) => {
    try {
      set({ loading: true, error: null });
      const response = await updateLocation(id, locationData);

      if (response.success) {
        // Refresh locations list
        await get().fetchLocations();
        return { success: true, message: response.message || 'Location updated successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to update location';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to update location';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  removeLocation: async (id: number) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteLocation(id);

      if (response.success) {
        // Refresh locations list
        await get().fetchLocations();
        return { success: true, message: response.message || 'Location deleted successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to delete location';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to delete location';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  // ===== ROOM CRUD =====

  addRoom: async (roomData: Partial<Room>) => {
    try {
      set({ loading: true, error: null });
      const response = await createRoom(roomData);

      if (response.success) {
        // Refresh rooms list
        await get().fetchRooms();
        return { success: true, message: response.message || 'Room created successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to create room';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to create room';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  editRoom: async (id: number, roomData: Partial<Room>) => {
    try {
      set({ loading: true, error: null });
      const response = await updateRoom(id, roomData);

      if (response.success) {
        // Refresh rooms list
        await get().fetchRooms();
        return { success: true, message: response.message || 'Room updated successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to update room';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to update room';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  removeRoom: async (id: number) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteRoom(id);

      if (response.success) {
        // Refresh rooms list
        await get().fetchRooms();
        return { success: true, message: response.message || 'Room deleted successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to delete room';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to delete room';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  // ===== TRAINER CRUD =====

  addTrainer: async (trainerData: Partial<Trainer>) => {
    try {
      set({ loading: true, error: null });
      const response = await createTrainer(trainerData);

      if (response.success) {
        // Refresh trainers list
        await get().fetchTrainers();
        return { success: true, message: response.message || 'Trainer created successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to create trainer';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to create trainer';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  editTrainer: async (id: number, trainerData: Partial<Trainer>) => {
    try {
      set({ loading: true, error: null });
      const response = await updateTrainer(id, trainerData);

      if (response.success) {
        // Refresh trainers list
        await get().fetchTrainers();
        return { success: true, message: response.message || 'Trainer updated successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to update trainer';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to update trainer';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  removeTrainer: async (id: number) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteTrainer(id);

      if (response.success) {
        // Refresh trainers list
        await get().fetchTrainers();
        return { success: true, message: response.message || 'Trainer deleted successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to delete trainer';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to delete trainer';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  // ===== INSTRUCTOR MANAGEMENT =====

  fetchAvailableInstructors: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAvailableInstructors();

      if (response.success && response.data) {
        set({ instructors: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch available instructors', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createTrainerFromUser: async (userId: string, trainerData?: Partial<Trainer>) => {
    try {
      set({ loading: true, error: null });
      const response = await addTrainerFromUser(userId, trainerData);

      if (response.success) {
        // Refresh trainers list to show newly created trainer
        await get().fetchTrainers();
        return { success: true, message: response.message || 'Trainer created from user successfully' };
      } else {
        const errorMsg = response.error?.message || 'Failed to create trainer from user';
        set({ error: errorMsg, loading: false });
        return { success: false, message: errorMsg };
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to create trainer from user';
      set({ error: errorMsg, loading: false });
      return { success: false, message: errorMsg };
    }
  },

  // ===== UTILITY =====

  clearError: () => {
    set({ error: null });
  },
}));

export default useSuperAdminILTStore;
